import {
  DeleteOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, message, Tooltip, Typography, Upload } from 'antd';
import React from 'react';
import { parkApi } from '@/services/bff/park.api';
import type { ServiceFileDownload } from '@/services/bff/types/park';
import styles from './EnterpriseFiles.module.less';

type Props = {
  value: string[];
  onChange?: (value: string[]) => void;
  onUploadingChange?: (uploading: boolean) => void;
  maxCount?: number;
};

const MAX_SIZE = 20 * 1024 * 1024;

const isPdf = (file: File) =>
  file.type === 'application/pdf' ||
  ((!file.type || file.type === 'application/octet-stream') &&
    /\.pdf$/i.test(file.name));

const formatSize = (size?: number) => {
  if (!size) return '';
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const EnterpriseFiles: React.FC<Props> = ({
  value,
  onChange,
  onUploadingChange,
  maxCount = 10,
}) => {
  const [files, setFiles] = React.useState<Record<string, ServiceFileDownload>>(
    {},
  );
  const [uploading, setUploading] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    value
      .filter((uuid) => !files[uuid])
      .forEach((uuid) => {
        void parkApi
          .getFile(uuid)
          .then((file) => {
            if (active) setFiles((current) => ({ ...current, [uuid]: file }));
          })
          .catch(() => {
            if (active) message.error('文件加载失败');
          });
      });
    return () => {
      active = false;
    };
  }, [value, files]);

  const upload = async (file: File) => {
    if (!isPdf(file)) {
      message.error('仅支持 PDF 文件');
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_SIZE) {
      message.error('单个 PDF 不能超过 20MB');
      return Upload.LIST_IGNORE;
    }
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const result = await parkApi.uploadFile(file, 'enterprise_profile');
      onChange?.([...value, result.uuid]);
      message.success('PDF 已上传');
    } catch {
      message.error('PDF 上传失败');
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
    return Upload.LIST_IGNORE;
  };

  return (
    <div className={styles.list}>
      {value.map((uuid) => {
        const file = files[uuid];
        const pdf = file?.mimeType === 'application/pdf';
        const image = Boolean(file?.mimeType?.startsWith('image/'));
        const Icon = pdf
          ? FilePdfOutlined
          : image
            ? FileImageOutlined
            : FileOutlined;
        return (
          <div className={styles.file} key={uuid}>
            <Icon
              className={
                pdf ? styles.pdfIcon : image ? styles.imageIcon : styles.icon
              }
            />
            <div className={styles.info}>
              {file?.url ? (
                <Typography.Link
                  href={file.url}
                  target="_blank"
                  rel="noreferrer"
                  ellipsis
                  title={file.fileName}
                  className={styles.name}
                >
                  {file.fileName}
                </Typography.Link>
              ) : (
                <Typography.Text type="secondary">加载中…</Typography.Text>
              )}
              {file?.fileSize ? (
                <Typography.Text type="secondary" className={styles.size}>
                  {formatSize(file.fileSize)}
                </Typography.Text>
              ) : null}
            </div>
            {onChange && (
              <Tooltip title="移除文件">
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  aria-label="移除文件"
                  onClick={() =>
                    onChange(value.filter((item) => item !== uuid))
                  }
                />
              </Tooltip>
            )}
          </div>
        );
      })}
      {onChange && value.length < maxCount && (
        <Upload
          accept=".pdf,application/pdf"
          showUploadList={false}
          beforeUpload={upload}
          disabled={uploading}
        >
          <Button icon={<PlusOutlined />} loading={uploading}>
            上传 PDF
          </Button>
        </Upload>
      )}
      {!onChange && !value.length && (
        <Typography.Text type="secondary">暂无文件</Typography.Text>
      )}
    </div>
  );
};

export default EnterpriseFiles;
